import { useCallback, useEffect } from 'react';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import Layout from '../Layout';
import useChatList from './hooks/useChatList';
import useCompletion from './hooks/useCompletion';

import Question from '../Question';
import useTransition from '../../hooks/useTransition';
import { trackInterviewViewed } from '../../analytics';
import Completion from './Completion';
import useInterview from '../../hooks/useInterview';
import useInterviewType from '../../hooks/useInterviewType';
import useTreatmentTransition from './hooks/useTreatmentTransition';
import useParentTransition from '../../hooks/useParentTransition';

import usePolarisActor from 'src/hooks/usePolarisActor';
import usePolaris from 'src/hooks/usePolaris';
import { SanityTreatmentPlan } from 'src/interviews/components/TreatmentPlan';

import { buildDxocInterview } from './utils';

import type { ITreatmentInterviewProps } from 'src/packages/polaris/types/interviews';

export const TreatmentInterviewContainer = ({
  commonCauses,
  conditionName,
  formLocation,
  isEmbedded,
  slug,
  onQuestionDisplay,
  onQuestionSubmit,
  polarisProps,
}: Pick<
  ITreatmentInterviewProps,
  | 'commonCauses'
  | 'conditionName'
  | 'formLocation'
  | 'isEmbedded'
  | 'slug'
  | 'onQuestionDisplay'
  | 'onQuestionSubmit'
  | 'polarisProps'
>) => {
  const { send, addInterview } = usePolaris();
  const subActor = usePolarisActor();
  const state = subActor?.getSnapshot();
  const transition = useTransition();
  const chatList = useChatList();
  const completion = useCompletion();
  const interview = useInterview();
  const interviewType = useInterviewType();
  const parentTransition = useParentTransition();
  const { shouldTransition, target } = useTreatmentTransition();
  const activeQuestion = chatList.items[chatList.active];
  const showTreatmentPlan = state?.context.showTreatmentPlan;

  useEffect(() => {
    // New question or result loaded
    onQuestionDisplay?.(chatList.active);
  }, [chatList.active, completion.results]);

  useEffect(() => {
    if (shouldTransition) {
      /*
       * If slug of target interview to transition into is not the same as
       * current slug of current interview, we must transition via related question
       * e.g. User is on Sx page, goes through treatment interview and down the path
       * of transitioning into a Dxoc interview for a Dx(GERD)
       * */
      if (!target?.includes(slug)) {
        addInterview(buildDxocInterview({ target, context: state.context }));
      }
      parentTransition({
        event: 'TRANSITION',
        id: target,
      });
    }
  }, [shouldTransition, send, slug, state.context.interviewType]);

  const trackViewed = useCallback(() => {
    if (interview.token) {
      trackInterviewViewed(interview.token, interviewType, formLocation);
    }
  }, [interview.token, interviewType, formLocation]);

  const handleExpanded = useCallback(() => {
    trackViewed();
  }, [trackViewed]);

  const impressionRef = useImpressionTracking(() => {
    if (polarisProps?.displayPolarisUI || polarisProps?.defaultExpanded) {
      trackViewed();
    }
  });

  const handleRestart = useCallback(() => {
    transition({
      event: 'RESET',
    });
  }, [transition]);

  const handleGoBack = useCallback(() => {
    transition({
      event: 'BACK',
    });
  }, [transition]);
  const showCompletionScreen = !!completion.results || state?.matches('fetchingTreatmentForSx');
  const showBackButton =
    activeQuestion?.index !== 0 &&
    (state?.matches('treatingCause') || state?.matches('treatingSymptoms'));
  const content = useCallback(() => {
    if (showTreatmentPlan) {
      return (
        <SanityTreatmentPlan
          plans={state.context.treatmentPlans}
          plan={state.context.selectedPlan}
        />
      );
    } else if (showCompletionScreen) {
      return (
        <Completion
          key={completion.query}
          conditionName={conditionName}
          handleGoBack={handleGoBack}
          polarisProps={polarisProps}
          slug={slug}
        />
      );
    } else {
      return (
        <Question
          commonCauses={commonCauses}
          introText={activeQuestion.summaryText}
          question={activeQuestion}
          onQuestionSubmit={onQuestionSubmit}
          shouldShowBackButton={showBackButton}
          slug={slug}
        />
      );
    }
  }, [
    activeQuestion,
    conditionName,
    commonCauses,
    completion.query,
    handleGoBack,
    onQuestionSubmit,
    polarisProps,
    showCompletionScreen,
    showTreatmentPlan,
    slug,
    state?.context.selectedPlan,
    state?.context.treatmentPlans,
  ]);
  if (!activeQuestion) {
    return null;
  }

  return (
    <div ref={impressionRef}>
      <Layout
        defaultExpanded={polarisProps?.defaultExpanded}
        handleExit={polarisProps?.onExit}
        handleGoBack={handleGoBack}
        isAccordionEmbeddedLayout={!!polarisProps}
        isEmbedded={isEmbedded}
        onExpanded={handleExpanded}
        showBackButton={showBackButton}
        showRestartButton={chatList.active !== 0 || showCompletionScreen}
        triggerRestart={handleRestart}
        title={polarisProps?.titleQuestion}
      >
        {content()}
      </Layout>
    </div>
  );
};

export default TreatmentInterviewContainer;
